import React, { useState, useEffect } from "react";
import { AssessmentType, Level } from "../../../../services/Dtos";
import { VStack, Box } from "@chakra-ui/react";
import { BiBookOpen } from "react-icons/bi";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import CustomAlert from "../../../CustomUi/CustomAlert";
import CustomModal from "../../../CustomUi/CustomModal";
import TextInput from "../../../CustomUi/TextInput";
import SelectInput from "../../../CustomUi/SelectInput";
import CustomNumberInput from "../../../CustomUi/CustomNumberInput";
import { useConfirmDialog } from "../../../../hooks/useConfirmDialog";
import { useFormChanges } from "../../../../hooks/useFormChanges";
import { useFormFieldHandler } from "../../../../hooks/useFormFieldHandler";
import { useAssessmentTypeService } from "../../../../hooks/Domain/useAssessmentTypeService";
import InlineSeparator from "../../../CustomUi/InlineSeparator";
import Info from "../../../CustomUi/Info";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  assessmentTypeToEdit: AssessmentType | null;
  assessmentLevel: Level | null;
  nextOrder: number;
}

const AssessmentTypeModal: React.FC<Props> = ({
  isOpen,
  onClose,
  assessmentTypeToEdit,
  assessmentLevel,
  nextOrder,
}) => {
  const {
    createAssessmentType,
    updateAssessmentType,
    deleteAssessmentType,
    openDeleteDialog,
    setOpenDeleteDialog,
    errors,
    clearErrors,
    isLoading,
  } = useAssessmentTypeService();

  const getInitialAssessmentTypeFormState = (): AssessmentType => ({
    id: "",
    name: "",
    shortName: "",
    maxScore: 0,
    order: nextOrder,
    levelId: assessmentLevel?.id || "",
    schoolId: "",
    createdAt: "",
    updatedAt: "",
  });

  const initialAssessmentTypeFormState = getInitialAssessmentTypeFormState();

  const { levels } = useSchoolsContext();
  const [assessmentTypeFormState, setAssessmentTypeFormState] =
    useState<AssessmentType>(initialAssessmentTypeFormState);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Initialize confirmation dialog
  const { confirmUnsavedChanges } = useConfirmDialog();

  // Define field configurations for form validation
  const fieldConfigs = {
    name: { type: "text" as const, required: true, maxLength: 100 },
    shortName: { type: "text" as const, required: true, maxLength: 5 },
    levelId: { type: "text" as const, required: true },
    maxScore: { type: "number" as const, required: true, min: 1 },
    order: { type: "number" as const, required: true, min: 1 },
  } as const;

  // Use the form changes hook
  const { hasChanges, validateField, validateAllFields, hasValidationErrors } =
    useFormChanges({
      fields: fieldConfigs,
      initialData: assessmentTypeToEdit,
      currentData: assessmentTypeFormState,
      isEditMode: !!assessmentTypeToEdit,
    });

  // Use the form field handler hook
  const { handleFieldChange } = useFormFieldHandler({
    setFormState: setAssessmentTypeFormState,
    setTouchedFields,
  });

  // Real-time validation
  const validateFieldByName = (name: string, value: any): string => {
    const fieldKey = name as keyof AssessmentType;
    return validateField(fieldKey, value);
  };

  useEffect(() => {
    if (isOpen) {
      clearErrors();
      setTouchedFields(new Set());
    }
  }, [isOpen, clearErrors]);

  useEffect(() => {
    if (assessmentTypeToEdit) {
      setAssessmentTypeFormState(assessmentTypeToEdit);
    } else {
      setAssessmentTypeFormState(getInitialAssessmentTypeFormState());
    }
    setTouchedFields(new Set());
  }, [assessmentTypeToEdit, assessmentLevel, isOpen]);

  const showFieldError = (fieldName: string): boolean => {
    const error = validateFieldByName(
      fieldName,
      assessmentTypeFormState[fieldName as keyof AssessmentType]
    );
    return !!(error && touchedFields.has(fieldName));
  };

  const getFieldError = (fieldName: string): string => {
    return validateFieldByName(
      fieldName,
      assessmentTypeFormState[fieldName as keyof AssessmentType]
    );
  };

  const currentValidationErrors = validateAllFields();
  const hasValidationErrorsValue = hasValidationErrors(currentValidationErrors);

  const handleSave = async () => {
    if (hasValidationErrorsValue || !hasChanges) return;

    if (assessmentTypeToEdit) {
      const response = await updateAssessmentType(
        assessmentTypeToEdit.id,
        assessmentTypeFormState
      );
      if (response.result.succeeded) {
        onClose();
      }
    } else {
      const response = await createAssessmentType(assessmentTypeFormState);
      if (response.result.succeeded) {
        onClose();
      }
    }
  };

  const handleDelete = async () => {
    if (!assessmentTypeToEdit) return;
    const response = await deleteAssessmentType(assessmentTypeToEdit.id);
    if (response.result.succeeded) {
      onClose();
    }
  };

  const handleClose = async () => {
    if (hasChanges && assessmentTypeToEdit) {
      const confirmed = await confirmUnsavedChanges();
      if (confirmed) {
        clearErrors();
        onClose();
      }
    } else {
      clearErrors();
      onClose();
    }
  };

  const levelOptions = levels.map((level) => ({
    label: level.levelName,
    value: level.id,
  }));

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        handleClose={handleClose}
        handleAction={handleSave}
        handleDelete={handleDelete}
        deleteMessage="All related assessment type records and result entries will be lost permanently"
        isLoading={isLoading}
        hasChanges={hasChanges}
        hasValidationErrors={hasValidationErrorsValue}
        isDisabled={!hasChanges || hasValidationErrorsValue || isLoading}
        title={
          assessmentTypeToEdit
            ? "Edit Assessment Type"
            : "Add New Assessment Type"
        }
        icon={BiBookOpen}
        mode={assessmentTypeToEdit ? "edit" : "add"}
        actionText={
          assessmentTypeToEdit ? "Save Changes" : "Save Assessment Type"
        }
        openDeleteDialog={openDeleteDialog}
        setOpenDeleteDialog={setOpenDeleteDialog}
      >
        {errors.length > 0 && (
          <VStack gap={2} mb={4}>
            {errors.slice(0, 3).map((error, index) => (
              <CustomAlert key={index} message={error} />
            ))}
          </VStack>
        )}

        <VStack gap={6} align="stretch">
          {/* Assessment Type Information Section */}
          <Box>
            <InlineSeparator label="Assessment Type Information" />
            <VStack gap={4}>
              <TextInput
                label="Full Name"
                name="name"
                type="text"
                value={assessmentTypeFormState.name}
                onChange={handleFieldChange}
                placeholder="Assessment Type Name"
                required
                error={showFieldError("name") ? getFieldError("name") : ""}
                invalid={showFieldError("name")}
              />

              <TextInput
                label="Short Name"
                name="shortName"
                type="text"
                value={assessmentTypeFormState.shortName}
                onChange={handleFieldChange}
                placeholder="Short Name (max 5 chars)"
                required
                error={
                  showFieldError("shortName") ? getFieldError("shortName") : ""
                }
                invalid={showFieldError("shortName")}
              />
            </VStack>
          </Box>

          {/* Scoring and Configuration Section */}
          <Box>
            <InlineSeparator label="Scoring and Configuration" />
            <VStack gap={4}>
              <CustomNumberInput
                label="Maximum Score"
                value={assessmentTypeFormState.maxScore.toString()}
                onChange={(value) => {
                  setAssessmentTypeFormState((prev) => ({
                    ...prev,
                    maxScore: parseInt(value, 10) || 0,
                  }));
                  setTouchedFields((prev) => new Set(prev).add("maxScore"));
                }}
                min={1}
                error={getFieldError("maxScore")}
                invalid={showFieldError("maxScore")}
                required
              />

              {assessmentTypeToEdit && (
                <SelectInput
                  label="Level"
                  name="levelId"
                  value={assessmentTypeFormState.levelId}
                  onChange={handleFieldChange}
                  collection={levelOptions}
                  placeholder="Select Level"
                  required
                  error={
                    showFieldError("levelId") ? getFieldError("levelId") : ""
                  }
                  invalid={showFieldError("levelId")}
                />
              )}

              <CustomNumberInput
                label="Order"
                value={assessmentTypeFormState.order.toString()}
                onChange={(value) => {
                  setAssessmentTypeFormState((prev) => ({
                    ...prev,
                    order: parseInt(value, 10) || 0,
                  }));
                  setTouchedFields((prev) => new Set(prev).add("order"));
                }}
                min={1}
                error={getFieldError("order")}
                invalid={showFieldError("order")}
                info={
                  <Info content="The order of the assessment type in a list of assessment types. This helps with arranging the assessment types the way you want." />
                }
              />
            </VStack>
          </Box>
        </VStack>
      </CustomModal>
    </>
  );
};

export default AssessmentTypeModal;
